// ui.jsx
// ─────────────────────────────────────────────────────────────────────────────
// DESIGN PRIMITIVES — Button, Card, Input, Badge, Stat, etc.
// Import these in every section. Built on tokens from ./theme.js + theme.css.
// All components are memoized and use stable refs where it matters.
// ─────────────────────────────────────────────────────────────────────────────

import React, { forwardRef, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { tokens } from "./theme";

const t = tokens;

// ═════════════════════════════════════════════════════════════════════════════
// HOOKS — small, dependency-free, respect prefers-reduced-motion
// ═════════════════════════════════════════════════════════════════════════════

// Tween from 0 → `target` over `duration` ms (cubic-out). Returns the current
// animated value. If reduced-motion is enabled OR target isn't finite, snaps
// directly to the target value.
export function useCountUp(target, { duration = 900, delay = 0 } = {}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const goal = Number(target);
    if (!Number.isFinite(goal)) { setValue(0); return; }
    if (typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValue(goal);
      return;
    }
    fromRef.current = value;
    startRef.current = null;

    const tick = (now) => {
      if (startRef.current === null) startRef.current = now + delay;
      const elapsed = now - startRef.current;
      if (elapsed < 0) { rafRef.current = requestAnimationFrame(tick); return; }
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(fromRef.current + (goal - fromRef.current) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, delay]);

  return value;
}

// One-shot mount transition. Returns `true` on the first paint after mount,
// so children can transition in from an initial state.
export function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return mounted;
}

// ═════════════════════════════════════════════════════════════════════════════
// REVEAL — staggered fade-up wrapper.
// Children mount opacity-0/y-8, transition to opacity-1/y-0 after `delay`ms.
// ═════════════════════════════════════════════════════════════════════════════
export const Reveal = memo(function Reveal({ children, delay = 0, style }) {
  const mounted = useMounted();
  return (
    <div style={{
      opacity: mounted ? 1 : 0,
      transform: mounted ? "translateY(0)" : "translateY(8px)",
      transition: `opacity 480ms cubic-bezier(0.4,0,0.2,1) ${delay}ms, transform 520ms cubic-bezier(0.34, 1.2, 0.64, 1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// EYEBROW — small uppercase caption above titles
// ═════════════════════════════════════════════════════════════════════════════
export const Eyebrow = memo(function Eyebrow({ children, color = t.color.accent }) {
  return (
    <div style={{
      fontFamily: t.font.body,
      fontSize: 10, fontWeight: 700,
      letterSpacing: "0.14em", textTransform: "uppercase",
      color,
      lineHeight: 1,
    }}>{children}</div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// SPARKLINE — tiny SVG curve. Points are 0–1 normalised vertical values.
// ═════════════════════════════════════════════════════════════════════════════
export const Sparkline = memo(function Sparkline({
  points = [], width = 96, height = 28,
  stroke = t.color.accent, fill,
}) {
  const fillColor = fill || `${t.color.accent}1F`;
  const path = useMemo(() => {
    if (!points.length) return { line: "", area: "" };
    const stepX = width / (points.length - 1 || 1);
    const xy = points.map((p, i) => [i * stepX, height - p * height]);
    const line = xy.map(([x, y], i) =>
      `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${line} L${width},${height} L0,${height} Z`;
    return { line, area };
  }, [points, width, height]);
  if (!points.length) return null;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true"
         style={{ display: "block", overflow: "visible" }}>
      <path d={path.area} fill={fillColor} />
      <path d={path.line} fill="none" stroke={stroke} strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={width}
        cy={height - (points[points.length - 1] * height)}
        r="2.5" fill={stroke}
      />
    </svg>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// PROGRESS RING — circular SVG with the accent gradient. Animates 0 → progress.
// ═════════════════════════════════════════════════════════════════════════════
let _ringGradId = 0;
export const ProgressRing = memo(function ProgressRing({
  progress = 0, size = 88, stroke = 8, label = "Progress",
}) {
  // Unique gradient ID per instance — multiple rings on a page would clash otherwise
  const gradId = useMemo(() => `ds-ring-grad-${++_ringGradId}`, []);
  const animated = useCountUp(progress, { duration: 1100, delay: 200 });
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, animated)) / 100) * c;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"  stopColor={t.color.accent} />
            <stop offset="100%" stopColor={t.color.primary} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke={t.color.border} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke={`url(#${gradId})`} strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={c}
                strokeDashoffset={offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: "stroke-dashoffset 80ms linear" }} />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column",
      }}>
        <div style={{
          fontFamily: t.font.mono,
          fontSize: Math.round(size * 0.20),
          fontWeight: 700,
          color: t.color.ink,
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
        }}>{Math.round(animated)}%</div>
        {label && (
          <div style={{
            marginTop: 2,
            fontSize: Math.max(7, Math.round(size * 0.085)),
            fontWeight: 700,
            letterSpacing: "0.16em", textTransform: "uppercase",
            color: t.color.inkSubtle,
          }}>{label}</div>
        )}
      </div>
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// DATA FIELD — read-mode label/value pair (compact, surfaceAlt background)
// ═════════════════════════════════════════════════════════════════════════════
export const DataField = memo(function DataField({ label, value, mono = false }) {
  return (
    <div style={{
      background: t.color.surfaceAlt,
      border: `1px solid ${t.color.border}`,
      borderRadius: t.radius.sm,
      padding: "12px 14px",
      minWidth: 0,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700,
        letterSpacing: "0.12em", textTransform: "uppercase",
        color: t.color.inkSubtle,
        lineHeight: 1,
      }}>{label}</div>
      <div style={{
        marginTop: 6,
        fontFamily: mono ? t.font.mono : t.font.body,
        fontSize: 13, fontWeight: 600,
        color: t.color.ink,
        letterSpacing: mono ? "-0.01em" : "-0.005em",
        wordBreak: "break-word",
        fontVariantNumeric: "tabular-nums",
      }}>{value || "—"}</div>
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// FORM GROUP DIVIDER — small section heading with the accent stripe motif
// ═════════════════════════════════════════════════════════════════════════════
export const FormGroupDivider = memo(function FormGroupDivider({ children }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      margin: "20px 0 4px",
    }}>
      <div style={{
        width: 4, height: 16, borderRadius: 2,
        background: `linear-gradient(180deg, ${t.color.accent}, ${t.color.primary})`,
      }} />
      <span style={{
        fontFamily: t.font.body,
        fontSize: 11, fontWeight: 700,
        letterSpacing: "0.14em", textTransform: "uppercase",
        color: t.color.primary,
      }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: t.color.border }} />
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// FORM ROW — 260px label column + helper, input takes the rest. Used in
// long edit forms. Different from `Field` (which is stacked).
// ═════════════════════════════════════════════════════════════════════════════
export const FormRow = memo(function FormRow({ label, helper, children }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "260px minmax(0, 1fr)",
      gap: 20, alignItems: "start",
      padding: "16px 0",
      borderBottom: `1px solid ${t.color.border}`,
    }}>
      <div>
        <div style={{
          fontSize: 10, fontWeight: 700,
          letterSpacing: "0.12em", textTransform: "uppercase",
          color: t.color.ink,
          lineHeight: 1,
          marginBottom: 6,
        }}>{label}</div>
        {helper && (
          <div style={{ fontSize: 12, color: t.color.inkMuted, lineHeight: 1.5 }}>
            {helper}
          </div>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// QUICK TILE — interactive navigation card (icon, title, blurb, CTA)
// On hover: lifts, the accent stripe slides across the top, icon goes solid
// orange, the CTA arrow translates right.
// ═════════════════════════════════════════════════════════════════════════════
export const QuickTile = memo(function QuickTile({ icon, title, text, buttonLabel, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      style={{
        position: "relative",
        background: t.color.surface,
        border: `1px solid ${hover ? t.color.primary : t.color.border}`,
        borderRadius: t.radius.md,
        padding: 20,
        cursor: "pointer",
        transition: `all ${t.motion.base}`,
        boxShadow: hover ? t.shadow.md : t.shadow.sm,
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        overflow: "hidden",
        outline: "none",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0,
        width: hover ? "100%" : 3, height: 3,
        background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 100%)`,
        transition: "width 280ms cubic-bezier(0.4,0,0.2,1)",
      }} />
      <div style={{
        width: 36, height: 36,
        borderRadius: t.radius.sm,
        background: hover ? t.color.accent : t.color.accentSoft,
        color: hover ? t.color.inkOnAccent : t.color.accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: `all ${t.motion.base}`,
      }}>{icon}</div>
      <div>
        <h4 style={{
          margin: 0,
          fontFamily: t.font.display,
          fontSize: 15, fontWeight: 700,
          letterSpacing: "-0.015em",
          color: t.color.ink,
        }}>{title}</h4>
        <p style={{
          margin: "6px 0 0",
          fontSize: 12, color: t.color.inkMuted,
          lineHeight: 1.5,
        }}>{text}</p>
      </div>
      <div style={{
        marginTop: "auto",
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 700,
        color: hover ? t.color.accent : t.color.inkMuted,
        letterSpacing: "0.08em", textTransform: "uppercase",
        transition: `color ${t.motion.base}`,
      }}>
        {buttonLabel}
        <span style={{
          transition: `transform ${t.motion.base}`,
          transform: hover ? "translateX(3px)" : "translateX(0)",
          display: "inline-block",
        }}>→</span>
      </div>
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// BLUEPRINT GRID — decorative dotted-grid background, used behind hero areas
// Apply as a child with `position: absolute; inset: 0` inside a relative parent.
// ═════════════════════════════════════════════════════════════════════════════
export const BlueprintGrid = memo(function BlueprintGrid({
  size = 32, opacity = 0.025, mask = true,
}) {
  return (
    <div aria-hidden="true" style={{
      position: "absolute", inset: -24, zIndex: 0,
      backgroundImage:
        `linear-gradient(rgba(14,17,22,${opacity}) 1px, transparent 1px), ` +
        `linear-gradient(90deg, rgba(14,17,22,${opacity}) 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
      ...(mask ? {
        maskImage: "radial-gradient(ellipse at top, rgba(0,0,0,0.6), transparent 70%)",
        WebkitMaskImage: "radial-gradient(ellipse at top, rgba(0,0,0,0.6), transparent 70%)",
      } : null),
      pointerEvents: "none",
    }} />
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// BUTTON — five variants × three sizes, with iconography support
// ═════════════════════════════════════════════════════════════════════════════
const buttonBase = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: t.space[2],
  fontFamily: t.font.body,
  fontWeight: 600,
  letterSpacing: "-0.005em",
  border: "1px solid transparent",
  borderRadius: t.radius.sm,
  cursor: "pointer",
  transition: `background ${t.motion.fast}, border ${t.motion.fast}, box-shadow ${t.motion.fast}, transform ${t.motion.fast}, color ${t.motion.fast}`,
  whiteSpace: "nowrap",
  userSelect: "none",
  textDecoration: "none",
  outline: "none",
};

const buttonSize = {
  sm: { fontSize: 12, padding: "6px 10px",  height: 28, gap: 6 },
  md: { fontSize: 13, padding: "8px 14px",  height: 34, gap: 8 },
  lg: { fontSize: 14, padding: "10px 18px", height: 40, gap: 8 },
};

const buttonVariant = {
  primary: {
    rest:  { background: t.color.primary,      color: t.color.inkOnDark, borderColor: t.color.primary, boxShadow: t.shadow.sm },
    hover: { background: t.color.primaryHover, borderColor: t.color.primaryHover, transform: "translateY(-1px)", boxShadow: t.shadow.md },
  },
  accent: {
    rest:  { background: t.color.accent,      color: t.color.inkOnAccent, borderColor: t.color.accent, boxShadow: t.shadow.sm },
    hover: { background: t.color.accentHover, borderColor: t.color.accentHover, transform: "translateY(-1px)", boxShadow: t.shadow.md },
  },
  outline: {
    rest:  { background: t.color.surface,    color: t.color.ink, borderColor: t.color.borderStrong },
    hover: { background: t.color.surfaceAlt, borderColor: t.color.primary, color: t.color.primary },
  },
  ghost: {
    rest:  { background: "transparent",     color: t.color.inkMuted, borderColor: "transparent" },
    hover: { background: t.color.surfaceAlt, color: t.color.ink },
  },
  danger: {
    rest:  { background: t.color.surface, color: t.color.danger, borderColor: t.color.dangerBorder },
    hover: { background: t.color.dangerSoft, borderColor: t.color.danger },
  },
  // Soft success — for affirmative inline actions like "View" or "Create VO".
  // Reads as "go" without competing with the page's primary accent CTA.
  success: {
    rest:  { background: t.color.surface,    color: t.color.success, borderColor: t.color.successBorder },
    hover: { background: t.color.successSoft, borderColor: t.color.success },
  },
  // Subtle — minimal weight inline button. Use for table-row "Edit" actions
  // where four buttons next to each other would otherwise feel shouty.
  subtle: {
    rest:  { background: t.color.surfaceAlt, color: t.color.ink, borderColor: t.color.border },
    hover: { background: t.color.surface,    borderColor: t.color.primary, color: t.color.primary },
  },
};

export const Button = memo(forwardRef(function Button(
  { children, variant = "primary", size = "md", disabled, type = "button",
    icon, iconRight, fullWidth, style: styleOverride, onClick, ...rest },
  ref
) {
  const [hover, setHover] = React.useState(false);
  const v = buttonVariant[variant] || buttonVariant.primary;
  const s = buttonSize[size] || buttonSize.md;

  const style = {
    ...buttonBase,
    ...s,
    ...(hover && !disabled ? v.hover : v.rest),
    ...(disabled ? { opacity: 0.5, cursor: "not-allowed", transform: "none", boxShadow: "none" } : null),
    ...(fullWidth ? { width: "100%" } : null),
    ...styleOverride,
  };

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={disabled ? undefined : onClick}
      style={style}
      {...rest}
    >
      {icon}
      <span>{children}</span>
      {iconRight}
    </button>
  );
}));

// ═════════════════════════════════════════════════════════════════════════════
// CARD — the foundational container. Has optional blueprint stripe accent.
// ═════════════════════════════════════════════════════════════════════════════
export const Card = memo(function Card({
  children, title, subtitle, action, accent, padded = true, style: styleOverride
}) {
  const cardStyle = useMemo(() => ({
    background: t.color.surface,
    border: `1px solid ${t.color.border}`,
    borderRadius: t.radius.lg,
    boxShadow: t.shadow.sm,
    position: "relative",
    overflow: "hidden",
    ...styleOverride,
  }), [styleOverride]);

  return (
    <section style={cardStyle}>
      {accent && (
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
          background: typeof accent === "string" ? accent : `linear-gradient(180deg, ${t.color.accent}, ${t.color.primary})`,
        }} />
      )}
      {(title || action) && (
        <header style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: `1px solid ${t.color.border}`,
          gap: 16, minHeight: 56,
        }}>
          <div style={{ minWidth: 0 }}>
            {title && (
              <h3 style={{
                margin: 0,
                fontFamily: t.font.display,
                fontSize: 15, fontWeight: 700, letterSpacing: "-0.015em",
                color: t.color.ink,
              }}>{title}</h3>
            )}
            {subtitle && (
              <div style={{
                marginTop: 2, fontSize: 12, color: t.color.inkMuted, fontWeight: 500,
              }}>{subtitle}</div>
            )}
          </div>
          {action && <div style={{ flexShrink: 0 }}>{action}</div>}
        </header>
      )}
      <div style={padded ? { padding: 20 } : null}>
        {children}
      </div>
    </section>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// BADGE — status pills, used everywhere
// ═════════════════════════════════════════════════════════════════════════════
const badgeVariant = {
  default: { bg: t.color.neutralSoft, fg: t.color.neutral, border: t.color.neutralBorder },
  primary: { bg: t.color.primarySoft, fg: t.color.primary, border: "#C0CDD8" },
  accent:  { bg: t.color.accentSoft,  fg: t.color.accent,  border: "#F5BFA3" },
  success: { bg: t.color.successSoft, fg: t.color.success, border: t.color.successBorder },
  warning: { bg: t.color.warningSoft, fg: t.color.warning, border: t.color.warningBorder },
  danger:  { bg: t.color.dangerSoft,  fg: t.color.danger,  border: t.color.dangerBorder },
  info:    { bg: t.color.infoSoft,    fg: t.color.info,    border: "#9CC4DF" },
};

export const Badge = memo(function Badge({ children, variant = "default", size = "md", icon }) {
  const v = badgeVariant[variant] || badgeVariant.default;
  const sizes = {
    sm: { fontSize: 10, padding: "2px 7px", gap: 4 },
    md: { fontSize: 11, padding: "3px 9px", gap: 5 },
    lg: { fontSize: 12, padding: "4px 12px", gap: 6 },
  };
  const s = sizes[size];

  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: s.gap,
      background: v.bg, color: v.fg,
      border: `1px solid ${v.border}`,
      borderRadius: t.radius.full,
      fontFamily: t.font.body,
      fontWeight: 600,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      ...s,
    }}>
      {icon}
      {children}
    </span>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// STAT — KPI / metric tile. The atomic unit of dashboards.
// Tabular numerals built in. Optional count-up animation, trend chip,
// sublabel/helper, sparkline. Pass `rawValue` + `format` for animation,
// or `value` (pre-formatted) for static.
// ═════════════════════════════════════════════════════════════════════════════
export const Stat = memo(function Stat({
  label, value, sublabel, helper, accent, trend, icon, mono = true,
  rawValue, format, sparkPoints, delay = 0,
}) {
  const trendColor = trend > 0 ? t.color.success : trend < 0 ? t.color.danger : t.color.inkMuted;
  const animated = useCountUp(Number.isFinite(rawValue) ? rawValue : 0,
    { duration: 1000, delay });
  const displayValue = Number.isFinite(rawValue) && typeof format === "function"
    ? format(animated)
    : value;
  const subText = sublabel || helper;

  return (
    <div style={{
      position: "relative",
      padding: "18px 20px",
      background: t.color.surface,
      border: `1px solid ${t.color.border}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.sm,
      overflow: "hidden",
      transition: `transform ${t.motion.base}, box-shadow ${t.motion.base}`,
    }}>
      {accent && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 3,
          background: accent,
        }} />
      )}

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{
          fontFamily: t.font.body,
          fontSize: 10, fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: t.color.inkMuted,
          lineHeight: 1,
        }}>{label}</div>
        {icon && <div style={{ color: t.color.inkSubtle, flexShrink: 0 }}>{icon}</div>}
      </div>

      <div style={{
        marginTop: 10,
        fontFamily: mono ? t.font.mono : t.font.display,
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: mono ? "-0.01em" : "-0.025em",
        color: t.color.ink,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.1,
        wordBreak: "break-word",
      }}>{displayValue}</div>

      {(subText || trend !== undefined || (sparkPoints && sparkPoints.length > 1)) && (
        <div style={{
          marginTop: 8,
          display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          gap: 12,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, fontWeight: 500, lineHeight: 1.45,
            flex: 1, minWidth: 0,
          }}>
            {trend !== undefined && (
              <span style={{ color: trendColor, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {trend > 0 ? "▲" : trend < 0 ? "▼" : "•"} {Math.abs(trend)}%
              </span>
            )}
            {subText && <span style={{ color: t.color.inkMuted }}>{subText}</span>}
          </div>
          {sparkPoints && sparkPoints.length > 1 && (
            <Sparkline points={sparkPoints} stroke={accent || t.color.accent}
              fill={`${(accent || t.color.accent)}1F`} width={72} height={22} />
          )}
        </div>
      )}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// STAT GRID — responsive arrangement of Stats
// ═════════════════════════════════════════════════════════════════════════════
export const StatGrid = memo(function StatGrid({ children, minWidth = 200 }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,
      gap: 12,
    }}>
      {children}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// INPUT — text, number, date. Uses native HTML input under the hood.
// ═════════════════════════════════════════════════════════════════════════════
const inputBase = {
  width: "100%",
  fontFamily: t.font.body,
  fontSize: 13,
  fontWeight: 500,
  color: t.color.ink,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.sm,
  padding: "8px 12px",
  height: 34,
  outline: "none",
  transition: `border-color ${t.motion.fast}, box-shadow ${t.motion.fast}, background ${t.motion.fast}`,
  fontVariantNumeric: "tabular-nums",
};

export const Input = memo(forwardRef(function Input(
  { invalid, mono, style, ...rest }, ref
) {
  return (
    <input
      ref={ref}
      style={{
        ...inputBase,
        ...(mono ? { fontFamily: t.font.mono, letterSpacing: "-0.01em" } : null),
        ...(invalid ? { borderColor: t.color.danger, background: t.color.dangerSoft } : null),
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = t.color.primary;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${t.color.accentSoft}`;
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = invalid ? t.color.danger : t.color.border;
        e.currentTarget.style.boxShadow = "none";
        rest.onBlur?.(e);
      }}
      {...rest}
    />
  );
}));

export const Select = memo(forwardRef(function Select({ children, style, ...rest }, ref) {
  return (
    <select
      ref={ref}
      style={{
        ...inputBase,
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%235D6470' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
        paddingRight: 32,
        cursor: "pointer",
        ...style,
      }}
      {...rest}
    >
      {children}
    </select>
  );
}));

export const Textarea = memo(forwardRef(function Textarea({ style, rows = 4, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      style={{
        ...inputBase,
        height: "auto",
        minHeight: 80,
        resize: "vertical",
        lineHeight: 1.5,
        padding: "10px 12px",
        ...style,
      }}
      {...rest}
    />
  );
}));

// ═════════════════════════════════════════════════════════════════════════════
// FIELD — label + helper + input grouping
// ═════════════════════════════════════════════════════════════════════════════
export const Field = memo(function Field({ label, helper, required, error, children, htmlFor }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      {label && (
        <label htmlFor={htmlFor} style={{
          fontFamily: t.font.body,
          fontSize: 11, fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: t.color.inkMuted,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}>
          {label}
          {required && <span style={{ color: t.color.accent }}>*</span>}
        </label>
      )}
      {children}
      {(helper || error) && (
        <div style={{
          fontSize: 11,
          color: error ? t.color.danger : t.color.inkSubtle,
          fontWeight: 500,
        }}>
          {error || helper}
        </div>
      )}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// TABLE — semantic, dense, tabular numerals built in
// ═════════════════════════════════════════════════════════════════════════════
export const Table = memo(function Table({ children, style }) {
  return (
    <div style={{
      borderRadius: t.radius.md,
      border: `1px solid ${t.color.border}`,
      overflow: "auto",
      background: t.color.surface,
      ...style,
    }}>
      <table style={{
        width: "100%",
        borderCollapse: "separate",
        borderSpacing: 0,
        fontFamily: t.font.body,
        fontSize: 12.5,
        fontVariantNumeric: "tabular-nums",
      }}>
        {children}
      </table>
    </div>
  );
});

export const Th = memo(function Th({ children, align = "left", sticky, width, style }) {
  return (
    <th style={{
      textAlign: align,
      padding: "10px 12px",
      background: t.color.surfaceAlt,
      borderBottom: `1px solid ${t.color.borderStrong}`,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: t.color.inkMuted,
      whiteSpace: "nowrap",
      position: sticky ? "sticky" : undefined,
      top: sticky ? 0 : undefined,
      zIndex: sticky ? 1 : undefined,
      width,
      ...style,
    }}>{children}</th>
  );
});

export const Td = memo(function Td({ children, align = "left", mono, accent, weight, style }) {
  return (
    <td style={{
      textAlign: align,
      padding: "10px 12px",
      borderBottom: `1px solid ${t.color.border}`,
      color: accent || t.color.ink,
      fontFamily: mono ? t.font.mono : t.font.body,
      fontWeight: weight || 500,
      ...style,
    }}>{children}</td>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// PAGE HEADER — the masthead of every section
// ═════════════════════════════════════════════════════════════════════════════
export const PageHeader = memo(function PageHeader({
  eyebrow, title, subtitle, badges, actions, breadcrumb
}) {
  return (
    <header style={{
      position: "relative",
      padding: "24px 24px 20px",
      background: t.color.surface,
      border: `1px solid ${t.color.border}`,
      borderRadius: t.radius.lg,
      boxShadow: t.shadow.sm,
      overflow: "hidden",
    }}>
      {/* Top accent line — signature element */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${t.color.accent} 0%, ${t.color.primary} 40%, ${t.color.primary} 100%)`,
      }} />

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {breadcrumb && (
            <div style={{
              fontSize: 11, fontFamily: t.font.mono, color: t.color.inkSubtle,
              marginBottom: 6, letterSpacing: "0.02em",
            }}>{breadcrumb}</div>
          )}
          {eyebrow && (
            <div style={{
              fontSize: t.text.eyebrow.size,
              fontWeight: t.text.eyebrow.weight,
              letterSpacing: t.text.eyebrow.tracking,
              textTransform: "uppercase",
              color: t.color.accent,
              marginBottom: 6,
            }}>{eyebrow}</div>
          )}
          <h1 style={{
            margin: 0,
            fontFamily: t.font.display,
            fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1,
            color: t.color.ink,
          }}>{title}</h1>
          {subtitle && (
            <p style={{
              margin: "8px 0 0",
              fontSize: 13, color: t.color.inkMuted, lineHeight: 1.55,
              maxWidth: 720,
            }}>{subtitle}</p>
          )}
          {badges && (
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {badges}
            </div>
          )}
        </div>
        {actions && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {actions}
          </div>
        )}
      </div>
    </header>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// TOOLBAR — search + filter + action grouping above tables
// ═════════════════════════════════════════════════════════════════════════════
export const Toolbar = memo(function Toolbar({ children, style }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "12px 16px",
      background: t.color.surface,
      border: `1px solid ${t.color.border}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.sm,
      ...style,
    }}>
      {children}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// EMPTY STATE — used when filtered results are empty
// ═════════════════════════════════════════════════════════════════════════════
export const Empty = memo(function Empty({ icon, title, message, action }) {
  return (
    <div style={{
      padding: "48px 24px",
      textAlign: "center",
      color: t.color.inkMuted,
    }}>
      {icon && <div style={{ marginBottom: 12, opacity: 0.6, fontSize: 32 }}>{icon}</div>}
      {title && (
        <div style={{
          fontFamily: t.font.display,
          fontSize: 16, fontWeight: 600, color: t.color.ink,
          marginBottom: 6, letterSpacing: "-0.015em",
        }}>{title}</div>
      )}
      {message && <div style={{ fontSize: 13, maxWidth: 360, margin: "0 auto 16px" }}>{message}</div>}
      {action}
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// DIVIDER — subtle horizontal rule with optional label
// ═════════════════════════════════════════════════════════════════════════════
export const Divider = memo(function Divider({ label, style }) {
  if (!label) {
    return <hr style={{ border: 0, borderTop: `1px solid ${t.color.border}`, margin: "16px 0", ...style }} />;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0", ...style }}>
      <div style={{ flex: 1, height: 1, background: t.color.border }} />
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.12em",
        textTransform: "uppercase", color: t.color.inkSubtle,
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: t.color.border }} />
    </div>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// CHECKBOX — custom-styled, accessible, tracks focus + checked state
// Renders a visible chip + a hidden real input for forms / accessibility.
// ═════════════════════════════════════════════════════════════════════════════
export const Checkbox = memo(function Checkbox({
  checked, onChange, disabled, label, size = "md", style,
}) {
  const boxSize  = size === "sm" ? 14 : 16;
  const tickSize = size === "sm" ? 9  : 10;
  return (
    <label style={{
      display: "inline-flex", alignItems: "center", gap: label ? 8 : 0,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      userSelect: "none",
      ...style,
    }}>
      <span style={{
        width: boxSize, height: boxSize, borderRadius: 3,
        border: `1.5px solid ${checked ? t.color.accent : t.color.borderStrong}`,
        background: checked ? t.color.accent : t.color.surface,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        transition: `all ${t.motion.fast}`,
      }}>
        {checked && (
          <svg width={tickSize} height={tickSize} viewBox="0 0 24 24" fill="none"
            stroke={t.color.inkOnAccent} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        )}
      </span>
      <input type="checkbox" checked={!!checked} disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }} />
      {label && (
        <span style={{
          fontSize: 13, fontWeight: 500,
          color: t.color.ink,
        }}>{label}</span>
      )}
    </label>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// CHIP — small dismissable / clickable label, for filter tags
// ═════════════════════════════════════════════════════════════════════════════
export const Chip = memo(function Chip({ children, onRemove, onClick, active, mono }) {
  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 8px",
        background: active ? t.color.primary : t.color.surfaceAlt,
        color: active ? t.color.inkOnDark : t.color.ink,
        border: `1px solid ${active ? t.color.primary : t.color.border}`,
        borderRadius: t.radius.xs,
        fontFamily: mono ? t.font.mono : t.font.body,
        fontSize: 11, fontWeight: 600,
        cursor: onClick ? "pointer" : "default",
        transition: `background ${t.motion.fast}, color ${t.motion.fast}`,
      }}
    >
      {children}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{
            border: 0, background: "transparent", cursor: "pointer",
            color: "inherit", opacity: 0.7, padding: 0, lineHeight: 1,
            fontSize: 14,
          }}
          aria-label="Remove"
        >×</button>
      )}
    </span>
  );
});

// ═════════════════════════════════════════════════════════════════════════════
// EXPORTS — convenience default
// ═════════════════════════════════════════════════════════════════════════════
const ui = {
  // Layout & cards
  Card, PageHeader, Toolbar, Empty, Divider,
  // Forms
  Input, Select, Textarea, Field, FormRow, FormGroupDivider,
  // Buttons & chips
  Button, Badge, Chip, Checkbox,
  // Data display
  Stat, StatGrid, DataField, Sparkline, ProgressRing,
  // Table
  Table, Th, Td,
  // Layout helpers
  Eyebrow, Reveal, BlueprintGrid, QuickTile,
  // Hooks (re-exported for direct use)
  useCountUp, useMounted,
};
export default ui;
